import {
  Chart,
  ChartBody,
  ChartContainer,
  ChartFooter,
  DateRange,
  getDomainPoints,
  ReferenceArea,
  SeriesType,
  ValueRange,
} from 'bold-ui/lib/components/Chart'
import { blue, gray, green, red, yellow } from 'bold-ui/lib/styles/colors'
import { SexoEnum } from 'graphql/types.generated'
import moment, { Duration, Moment } from 'moment'
import React from 'react'
import { ImcRange, ParametrosImcAdulto, ParametrosImcIdosoEGestante } from 'util/atendimento'
import { getParametrosParaSemanaGestacional } from 'util/atendimento/gestante/calculateEstadoNutricionalGestante'
import { calculateIdadeGestacionalParaGraficos } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { filterGestacoesPeriodo } from 'util/atendimento/gestante/filterGestacoesPeriodo'
import imcAdulto from 'util/atendimento/parametros/imcAdulto.json'
import imcIdoso from 'util/atendimento/parametros/imcIdoso.json'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { PeriodoGestacaoModel } from '../../../types/PeriodoGestacaoModel'
import { reduceMedicoesIguais } from '../../util-medicoes'
import { LinhaTooltipGrafico } from '../components/LinhaTooltipGrafico'
import { getReferenceAreasCalculators, ReferenceAreasCalculator } from './referenceAreaCalculators'
import { TickIdade } from './TickIdade'

export interface GraficoImcProps {
  medicoes: ReadonlyArray<HistoricoMedicaoModel>
  dataNascimento: Moment
  dataRange: DateRange
  sexo: SexoEnum
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isGestante: boolean
}

type ReferenceAreaStrategy = (
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment,
  gestacoes: PeriodoGestacaoModel[]
) => ReferenceArea<Moment>[]

// TODO@CODEBENDERS - Adicionar funcionalidade de outliers #23674
export function GraficoImcView(props: GraficoImcProps) {
  // TODO@CODEBENDERS - Colocar componente dentro do escopo do AtendimentoProvider #25821
  const { medicoes, dataNascimento, dataRange, sexo, gestacoes, isGestante } = props

  const maxIdadeNoRange = moment.duration(moment(dataRange.end).diff(dataNascimento))
  const medicoesComImc = reduceMedicoesIguais(medicoes, 'valorImc') ?? []
  const seriesData = medicoesComImc.map((m) => ({
    x: m.dataMedicao,
    y: m.valorImc,
  }))
  const yRange = getYRange(maxIdadeNoRange, isGestante)
  const gestacoesPeriodo = filterGestacoesPeriodo(gestacoes, moment(dataRange.init), moment(dataRange.end))
  const getAreas = resolveReferenceAreaStrategy(maxIdadeNoRange, isGestante)
  const referenceAreas = getAreas(sexo, dataRange, maxIdadeNoRange, dataNascimento, gestacoesPeriodo)

  return (
    <ChartContainer>
      <ChartBody height={500}>
        <Chart<Moment>
          type={SeriesType.Area}
          series={[{ name: 'IMC', data: seriesData }]}
          referenceAreas={referenceAreas}
          xAxis={{
            title: 'Idade (meses completos e anos)',
            domain: dataRange,
            tickRenderer: (props) => <TickIdade {...props} dataNascimento={dataNascimento} />,
          }}
          rangeAreas={gestacoesPeriodo.map((g) => ({
            name: 'Gestação',
            init: moment(g.inicio).startOf('day'),
            end: moment(g.fim ?? moment(g.inicio).add(42, 'week')).endOf('day'),
            fillColor: 'none',
            tickColor: blue.c60,
            strokeColor: gray.c40,
          }))}
          yAxis={{ title: 'IMC', unit: 'kg/m²', domain: yRange }}
          showLegend={false}
          tooltip={{
            type: 'point',
            render: (points) => {
              if (!points || !points.length) return
              const momentX = moment(points[0].x)
              const { idadeGestacional, hasGestacaoPeriodo } = calculateIdadeGestacionalParaGraficos(gestacoes, momentX)
              return (
                <>
                  {points?.map((p) => (
                    <LinhaTooltipGrafico key={p.seriesName}>{`IMC: ${p.y} kg/m²`}</LinhaTooltipGrafico>
                  ))}
                  {hasGestacaoPeriodo && (
                    <LinhaTooltipGrafico>
                      {`IG: ${idadeGestacional} ${idadeGestacional === 1 ? 'semana' : 'semanas'}`}
                    </LinhaTooltipGrafico>
                  )}
                  <LinhaTooltipGrafico>{`Data: ${momentX.format('DD/MM/YYYY')}`}</LinhaTooltipGrafico>
                </>
              )
            },
          }}
        />
      </ChartBody>
      <ChartFooter>
        {getFooterText(sexo, maxIdadeNoRange, dataNascimento, gestacoes, isGestante, !!gestacoesPeriodo.length)}
      </ChartFooter>
    </ChartContainer>
  )
}

function getFooterText(
  sexo: SexoEnum,
  maxIdadeNoRange: Duration,
  dataNascimento: Moment,
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>,
  isGestante: boolean,
  hasGestacaoPeriodo: boolean
): string {
  const idadeMax = maxIdadeNoRange.years()
  const isMasculino = sexo === SexoEnum.MASCULINO
  const isFeminino = sexo === SexoEnum.FEMININO
  const complementoGestante = isGestante && hasGestacaoPeriodo ? ' e Caderneta da Gestante - 2018' : ''
  const hasGestacaoNaFaseAdulta =
    isGestante && gestacoes.some((g) => moment(g.inicio).diff(dataNascimento, 'years') > 19)

  const faixaCaderneta = (masc: string, fem: string) => {
    if (isMasculino) return masc
    if (isFeminino) return `${fem}${complementoGestante}.`
    return ''
  }

  if (idadeMax <= 10) {
    return faixaCaderneta(
      'Fonte: Caderneta de Saúde da Criança Menino - 2020.',
      'Fonte: Caderneta de Saúde da Criança Menina - 2020'
    )
  }

  if (idadeMax <= 19) {
    return faixaCaderneta(
      'Fonte: Caderneta de Saúde do Adolescente - 2013.',
      'Fonte: Caderneta de Saúde da Adolescente - 2013'
    )
  }

  if (hasGestacaoNaFaseAdulta) {
    return 'Fonte: Caderneta da Gestante - 2018.'
  }

  return 'Fonte: Ministério da Saúde - 2022.'
}

function getYRange(maxIdadeNoRange: Duration, isGestante: boolean): ValueRange {
  if (maxIdadeNoRange.years() <= 2) return { init: 5, end: 25, step: 1 }
  else if (maxIdadeNoRange.years() <= 5) return { init: 10, end: 25, step: 1 }
  else if (maxIdadeNoRange.years() <= 10) return { init: 10, end: 30, step: 1 }
  else if (maxIdadeNoRange.years() <= 19 || isGestante) return { init: 10, end: 40, step: 2 }
  else return { init: 10, end: 46, step: 2 }
}

function resolveReferenceAreaStrategy(idadeNoRange: Duration, isGestante: boolean): ReferenceAreaStrategy {
  return isGestante && idadeNoRange.years() > 19 ? getReferenceAreasForGestante : getReferenceAreasForNaoGestante
}

function getReferenceAreasForGestante(
  _sexo: SexoEnum,
  xRange: DateRange,
  _idadeNoRange: Duration,
  _dataNascimento: Moment,
  gestacoes: PeriodoGestacaoModel[]
): ReferenceArea<Moment>[] {
  return [
    getReferenceAreaAdultoBaixoPeso(xRange, gestacoes),
    getReferenceAreaAdultoAdequado(xRange, gestacoes),
    getReferenceAreaAdultoSobrepeso(xRange, gestacoes),
    getReferenceAreaAdultoObesidade(xRange, gestacoes),
  ]
}

function getReferenceAreasForNaoGestante(
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment>[] {
  if (idadeNoRange.years() <= 19) {
    const refAreaCalculators = getReferenceAreasCalculators(sexo, dataNascimento, xRange, 'imc')
    return [
      getReferenceAreaCriancaMagrezaAcentuada(refAreaCalculators, idadeNoRange, gestacoesNoRange),
      getReferenceAreaCriancaMagreza(refAreaCalculators, idadeNoRange, gestacoesNoRange),
      getReferenceAreaCriancaEutrofia(refAreaCalculators, idadeNoRange, gestacoesNoRange),
      getReferenceAreaCriancaSobrepeso(refAreaCalculators, idadeNoRange, gestacoesNoRange),
      getReferenceAreaCriancaObesidade(refAreaCalculators, idadeNoRange, gestacoesNoRange),
      getReferenceAreaCriancaObesidadeGrave(refAreaCalculators, idadeNoRange, gestacoesNoRange),
    ]
  } else if (idadeNoRange.years() <= 60) {
    return [
      getReferenceAreaAdultoBaixoPeso(xRange, gestacoesNoRange),
      getReferenceAreaAdultoAdequado(xRange, gestacoesNoRange),
      getReferenceAreaAdultoSobrepeso(xRange, gestacoesNoRange),
      getReferenceAreaAdultoObesidade1(xRange),
      getReferenceAreaAdultoObesidade2(xRange),
      getReferenceAreaAdultoObesidade3(xRange),
    ]
  } else {
    return [
      getReferenceAreaIdosoBaixoPeso(xRange, gestacoesNoRange),
      getReferenceAreaIdosoAdequado(xRange, gestacoesNoRange),
      getReferenceAreaIdosoSobrepeso(xRange, gestacoesNoRange),
    ]
  }
}

function getReferenceAreaIdosoBaixoPeso(
  xRange: DateRange,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit:
        getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'baixo') ??
        getReferenceAreaUpperLimitIdoso('baixo'),
    })),
    label: {
      name: 'Baixo peso',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaIdosoAdequado(
  xRange: DateRange,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit:
        getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'adequado') ??
        getReferenceAreaUpperLimitIdoso('adequado'),
    })),
    label: {
      name: 'Adequado ou Eutrófico',
    },
    color: green.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaIdosoSobrepeso(
  xRange: DateRange,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit:
        getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'sobrepeso') ??
        getReferenceAreaUpperLimitIdoso('sobrepeso'),
    })),
    label: {
      name: 'Sobrepeso',
    },
    color: red.c90,
    stroke: {
      show: false,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoBaixoPeso(
  xRange: DateRange,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit:
        getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'baixo') ??
        getReferenceAreaUpperLimitAdulto('baixo'),
    })),
    label: {
      name: 'Baixo peso',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoAdequado(
  xRange: DateRange,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit:
        getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'adequado') ??
        getReferenceAreaUpperLimitAdulto('adequado'),
    })),
    label: {
      name: 'Adequado ou Eutrófico',
    },
    color: green.c90,
    stroke: {
      color: yellow.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaAdultoSobrepeso(
  xRange: DateRange,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit:
        getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'sobrepeso') ??
        getReferenceAreaUpperLimitAdulto('sobrepeso'),
    })),
    label: {
      name: 'Sobrepeso',
    },
    color: yellow.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: yellow.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade(
  xRange: DateRange,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'obesidade'),
    })),
    label: {
      name: 'Obesidade',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade1(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('obesidade1'),
    })),
    label: {
      name: 'Obesidade grau 1',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade2(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('obesidade2'),
    })),
    label: {
      name: 'Obesidade grau 2',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade3(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('obesidade3'),
    })),
    label: {
      name: 'Obesidade grau 3',
    },
    color: red.c90,
    stroke: {
      show: false,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaCriancaMagrezaAcentuada(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit:
        (idadeNoRange.years() > 10
          ? getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'muitoBaixo')
          : null) ?? referenceAreaCalculator(-3),
    })),
    label: {
      name: 'Magreza acentuada',
    },
    color: gray.c90,
    stroke: {
      color: gray.c40,
    },
    tick: {
      color: gray.c40,
    },
  }
}

function getReferenceAreaCriancaMagreza(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit:
        (idadeNoRange.years() > 10
          ? getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'baixo')
          : null) ?? referenceAreaCalculator(-2),
    })),
    label: {
      name: 'Magreza',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaCriancaEutrofia(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit:
        (idadeNoRange.years() > 10
          ? getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'adequado')
          : null) ?? referenceAreaCalculator(1),
    })),
    label: {
      name: 'Eutrófico',
    },
    color: green.c90,
    stroke: {
      color: yellow.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaCriancaSobrepeso(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit:
        (idadeNoRange.years() > 10
          ? getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'sobrepeso')
          : null) || referenceAreaCalculator(2),
    })),
    label: {
      name: idadeNoRange.years() <= 5 ? 'Risco de sobrepeso' : 'Sobrepeso',
    },
    color: yellow.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: yellow.c60,
    },
  }
}

function getReferenceAreaCriancaObesidade(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit:
        (idadeNoRange.years() > 10
          ? getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'obesidade')
          : null) ?? referenceAreaCalculator(3),
    })),
    label: {
      name: idadeNoRange.years() <= 5 ? 'Sobrepeso' : 'Obesidade',
    },
    color: red.c90,
    stroke: {
      color: gray.c40,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaCriancaObesidadeGrave(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration,
  gestacoesNoRange: PeriodoGestacaoModel[]
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
      upperLimit:
        idadeNoRange.years() > 10
          ? getReferenceAreaUpperLimitGestanteIfInGestacao(date, gestacoesNoRange, 'muitoAlto')
          : undefined,
    })),
    label: {
      name: idadeNoRange.years() <= 5 ? 'Obesidade' : 'Obesidade grave',
    },
    color: gray.c90,
    stroke: {
      show: false,
    },
    tick: {
      color: gray.c40,
    },
  }
}

function getSemanaGestacional(gestacao: PeriodoGestacaoModel, date: Moment) {
  return Math.min(Math.round(date.diff(moment(gestacao.inicio), 'week')), 42)
}

function getReferenceAreaUpperLimitGestanteIfInGestacao(
  date: Moment,
  gestacoes: PeriodoGestacaoModel[],
  type: keyof ParametrosImcIdosoEGestante | 'muitoBaixo' | 'muitoAlto'
): number | 'yInit' | 'yEnd' {
  const momentDate = moment(date)
  const gestacao = gestacoes.find((g) => momentDate.isBetween(g.inicio, g.fim ?? moment(g.inicio).add(42, 'week')))
  if (!gestacao) return
  const semanaGestacional = getSemanaGestacional(gestacao, momentDate)
  if (semanaGestacional >= 6 && type === 'muitoBaixo') return 'yInit'
  if (semanaGestacional >= 6 && type === 'muitoAlto') return 'yEnd'
  const range = getParametrosParaSemanaGestacional(semanaGestacional)?.[type]
  return range ? range.maximo ?? 'yEnd' : undefined
}

const getReferenceAreaUpperLimitAdulto = (type: keyof ParametrosImcAdulto) => (imcAdulto[type] as ImcRange)?.maximo

const getReferenceAreaUpperLimitIdoso = (type: keyof ParametrosImcIdosoEGestante) =>
  (imcIdoso[type] as ImcRange)?.maximo
